<template>
    <div class="show-change-room-list">
        <span class="show-change-room-list-btn" @click="showChangeRoomList">
            <i class="fal fa-sync"></i>
        </span>

        <div class="change-room-list" :class="{ shown: isSelectRoomListVisible }">
            <strong>{{ translations.roomPlan.room }}</strong>
            <div v-for="room of roomsToSelect" :key="room.id" class="change-room-item" @click="selectRoom(room)">
                {{ room.name }}
            </div>
        </div>
    </div>

    <Teleport v-if="isSelectRoomListVisible" :to="overlayTarget">
        <div
            class="navdim change-room-dim"
            :class="{ shown: isOverlayVisible }"
            @click.prevent.stop="closeChangeRoomList"
        />
    </Teleport>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { RoomDao } from "@/data/dao/service/RoomDao";
    import type { IRoomWithService } from "@/data/model/Room";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { nextTick, onMounted, ref, watch } from "vue";

    translations;

    const props = defineProps<{
        locationId: string;
        currentRoomId: string;
        overlayTarget: string;
    }>();

    const emit = defineEmits(["roomSelected"]);

    const roomDao = Factory.getInstance<RoomDao>(RoomDao);
    const roomsToSelect = ref<IRoomWithService[]>([]);
    const isSelectRoomListVisible = ref<boolean>(false);
    const isOverlayVisible = ref<boolean>(false);

    async function loadRoomsList() {
        try {
            const result = await roomDao.getData({
                locationId: props.locationId,
            });
            roomsToSelect.value = result.items.filter((r) => r.id != props.currentRoomId);
        } catch (ex) {
            logError(ex);
        }
    }

    function selectRoom(room: IRoomWithService) {
        emit("roomSelected", room);
        closeChangeRoomList();
    }

    async function showChangeRoomList() {
        isSelectRoomListVisible.value = true;
        await nextTick();
        isOverlayVisible.value = true;
    }

    function closeChangeRoomList() {
        isSelectRoomListVisible.value = false;
        isOverlayVisible.value = false;
    }

    onMounted(() => loadRoomsList());
    watch(() => props.locationId, loadRoomsList);
    watch(() => props.currentRoomId, loadRoomsList);

    // define exposed
    defineExpose({
        close: closeChangeRoomList,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .show-change-room-list {
        display: inline-block;
        margin-left: 1em;
        vertical-align: middle;
        position: relative;

        .show-change-room-list-btn {
            font-size: 18px;
            color: colors.$white;
            display: inline-block;
            cursor: pointer;
            width: 38px;
            height: 34px;
            background-color: colors.$green;
            line-height: 34px;
            text-align: center;
            border-radius: 3px;
            transition: 0.15s linear;

            &:hover,
            &:active {
                border-color: colors.$lightGreyBorderHover;
                background-color: colors.$greenHover;
                transform: rotate(90);
            }
        }

        .change-room-list {
            text-align: left;
            position: absolute;
            bottom: -5px;
            right: 0;
            display: none;
            padding-top: 1em;
            border-top-left-radius: 1em;
            border-top-right-radius: 1em;
            border: 1px solid colors.$greyBorder;
            background-color: colors.$white;
            z-index: 1000;

            -webkit-box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.2);
            -moz-box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.2);
            box-shadow: 0 0 5px 0px rgba(0, 0, 0, 0.2);

            &.shown {
                display: block;
            }

            strong {
                padding: 0.75em 1.5em;
            }

            .change-room-item {
                position: relative;
                min-height: 1.375em;
                display: block;
                text-decoration: none;
                padding: 0.75em 1.5em;
                font-size: 16px;
                font-weight: 300;
                color: #484848;
                line-height: 22px;
                white-space: nowrap;
                cursor: pointer;

                border-top: 1px solid colors.$lightGreyBorder;

                &:hover,
                &:active {
                    background-color: #fafafa;
                    color: #8fb52c;
                }
            }
        }
    }

    .change-room-dim.shown {
        opacity: 1 !important;
    }
</style>
